import type { AccountType } from "@cosmosai/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext } from "react";
import { useRuntimeConfig } from "../../app/config-context";
import type { AuthPort } from "./auth-port";

const AuthContext = createContext<AuthPort | null>(null);

export function AuthProvider({ auth, children }: { auth: AuthPort; children: ReactNode }) {
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

/** The sign-in port built at start-up; throws outside an AuthProvider. */
export function useAuth(): AuthPort {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error("useAuth needs an AuthProvider");
  return auth;
}

/** The signed-in identity, read once per page load. */
export function useIdentity() {
  const auth = useAuth();
  return useQuery({ queryKey: ["identity"], queryFn: () => auth.current(), staleTime: Infinity });
}

/** The caller's account type, for display only; "user" until the identity is known. */
export function useAccountType(): AccountType {
  return useIdentity().data?.accountType ?? "user";
}

/** Keycloak's account console (password, authenticator, sessions); null without Keycloak. */
export function useAccountConsoleUrl(): string | null {
  const { oidc } = useRuntimeConfig();
  return oidc ? `${oidc.authority.replace(/\/+$/, "")}/account` : null;
}

/** Signs out: forgets every cached answer, then ends the Keycloak session. */
export function useSignOut(): () => Promise<void> {
  const auth = useAuth();
  const queries = useQueryClient();
  return async () => {
    queries.clear();
    await auth.signOut();
  };
}
